// Centralized async-loaded clients store, backed by Supabase via /api/data/clients.
//
// Migration safety:
//   On first mount, if the API returns zero clients AND localStorage still
//   has the old `bcc_clients` blob, we bulk-upsert it to Supabase so nothing
//   is lost. After successful migration we mark localStorage as migrated
//   (but keep the data as a local read-only safety net for one more boot).
//
// Backward compatibility:
//   We keep the legacy synchronous `loadClients()` / `saveClients()`
//   exports working by writing the latest array into a module-level
//   cache that they read from. Components can be migrated to the
//   `useClients()` hook one at a time.
//
// Concurrency: last write wins. Every save also stamps the local cache
// before sending to the server, so optimistic UI feels snappy.

import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { apiFetch } from '../utils/apiFetch';
import { setClientsCache, getClientsCache } from '../utils/clientStore';

const ClientsContext = createContext(null);

const LEGACY_LS_KEY = 'bcc_clients';
const MIGRATED_FLAG = 'bcc_clients_migrated_to_supabase_at';

async function fetchAllClients() {
  const res = await apiFetch('/api/data/clients');
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch clients');
  return data.clients || [];
}

async function bulkUpsertClients(list) {
  const res = await apiFetch('/api/data/clients/bulk-upsert', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(list),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to bulk-upsert clients');
  return data.count || 0;
}

async function putClient(client) {
  const res = await apiFetch(`/api/data/clients/${encodeURIComponent(client.id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(client),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update client');
  return data.client;
}

async function postClient(client) {
  const res = await apiFetch('/api/data/clients', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(client),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create client');
  return data.client;
}

async function deleteClient(id) {
  const res = await apiFetch(`/api/data/clients/${encodeURIComponent(id)}`, { method: 'DELETE' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete client');
  return data;
}

export function ClientsProvider({ children }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [migrated, setMigrated] = useState(false);
  const [migrationCount, setMigrationCount] = useState(0);
  const initialized = useRef(false);

  // Initial load + auto-recovery: if API is empty AND localStorage has a
  // backup, restore from local. This runs on every mount (not just first
  // ever) so an accidental DB wipe self-heals from any user's local
  // shadow. The check is cheap; we only do work when API is empty.
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      try {
        let live = await fetchAllClients();

        if (live.length === 0) {
          // Could be: first-time setup OR an accidental wipe. Either way,
          // restore from localStorage if we have it.
          let stash = null;
          try { stash = JSON.parse(localStorage.getItem(LEGACY_LS_KEY) || 'null'); } catch {}
          if (Array.isArray(stash) && stash.length > 0) {
            const wasMigratedBefore = !!localStorage.getItem(MIGRATED_FLAG);
            console.warn(
              wasMigratedBefore
                ? `[ClientsContext] DB is empty but localStorage has ${stash.length} clients — auto-restoring (looks like a wipe).`
                : `[ClientsContext] First-time migration of ${stash.length} clients from localStorage to Supabase.`
            );
            await bulkUpsertClients(stash);
            localStorage.setItem(MIGRATED_FLAG, new Date().toISOString());
            setMigrated(true);
            setMigrationCount(stash.length);
            live = await fetchAllClients();
          }
        }

        setClients(live);
        setClientsCache(live);
        setLoading(false);
      } catch (e) {
        console.error('[ClientsContext] Failed to load clients:', e);
        try {
          const stash = JSON.parse(localStorage.getItem(LEGACY_LS_KEY) || 'null');
          if (Array.isArray(stash)) {
            setClients(stash);
            setClientsCache(stash);
          }
        } catch {}
        setError(e.message);
        setLoading(false);
      }
    })();
  }, []);

  // ─── Mutations ─────────────────────────────────────────────────────

  const refresh = useCallback(async () => {
    try {
      const live = await fetchAllClients();
      setClients(live);
      setClientsCache(live);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  // Optimistically update local state, then persist via API.
  // Pass a function (prev) => next OR an array.
  //
  // SAFETY: refuse to "wipe" — if we would persist [] but currently have
  // clients, treat it as a bug (not a real intent) and log the stack so
  // we can find the caller. The user can clear individual clients via
  // delete; there's no UI flow that legitimately wipes all of them.
  const replaceClients = useCallback(async (next) => {
    const resolved = typeof next === 'function' ? next(clients) : next;
    if (!Array.isArray(resolved)) {
      console.error('[ClientsContext] replaceClients received non-array:', resolved);
      return;
    }
    if (resolved.length === 0 && clients.length > 0) {
      console.error('[ClientsContext] Refusing to wipe', clients.length, 'clients. Stack:', new Error().stack);
      return;
    }
    setClients(resolved);
    setClientsCache(resolved);
    try {
      await bulkUpsertClients(resolved);
    } catch (e) {
      console.error('[ClientsContext] bulk-upsert failed, refreshing from server:', e);
      refresh();
    }
  }, [clients, refresh]);

  const saveOne = useCallback(async (client) => {
    const exists = clients.some(c => c.id === client.id);
    const optimistic = exists
      ? clients.map(c => c.id === client.id ? { ...c, ...client } : c)
      : [...clients, client];
    setClients(optimistic);
    setClientsCache(optimistic);
    try {
      const saved = exists ? await putClient(client) : await postClient(client);
      const reconciled = optimistic.map(c => c.id === saved.id ? { ...c, ...saved } : c);
      setClients(reconciled);
      setClientsCache(reconciled);
      return saved;
    } catch (e) {
      console.error('[ClientsContext] save failed:', e);
      refresh();
      throw e;
    }
  }, [clients, refresh]);

  const removeOne = useCallback(async (id) => {
    const optimistic = clients.filter(c => c.id !== id);
    setClients(optimistic);
    setClientsCache(optimistic);
    try {
      await deleteClient(id);
    } catch (e) {
      console.error('[ClientsContext] delete failed:', e);
      refresh();
      throw e;
    }
  }, [clients, refresh]);

  const value = {
    clients,
    loading,
    error,
    migrated,
    migrationCount,
    refresh,
    replaceClients,
    saveOne,
    removeOne,
  };

  return <ClientsContext.Provider value={value}>{children}</ClientsContext.Provider>;
}

export function useClients() {
  const ctx = useContext(ClientsContext);
  if (!ctx) throw new Error('useClients must be used within ClientsProvider');
  return ctx;
}

// Read the latest cached clients without subscribing — for legacy sync callers
// (e.g., FinancialDataContext computeCommittedTaxPrep). Returns whatever the
// provider last wrote, or [] if it hasn't loaded yet.
export function getCachedClients() {
  return getClientsCache();
}
